import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CustomModal from '../../common/CustomModal';
import CustomButton from '../../../components/CustomButton';
import InfoTooltip from '../../../components/InfoTooltip';
import LabelWithHelp from '../../../components/LabelWithHelp';
import ImageSlot from '../../../components/ImageSlot';
import CreateCodeModal from './CreateCodeModal';
import { makeHasPermissionSelector } from '../../../selector/authenticationSelector';
import Permission from '../../../common/permissions';
import {
    useCreateBannerMutation,
    useUpdateBannerMutation,
} from '../../../api/banner/bannerApiSlice';
import { AdminBanner, BannerType } from '../../../api/banner/bannerApiTypes';
import { useGetVouchersQuery } from '../../../api/voucher/voucherApiSlice';
import { UseCase } from '../../../tenants/config';
import { useTenantCapabilities } from '../../../hooks/useTenantCapabilities';
import { instantToLocalDateInputValue } from '../../../utils/dateTimeUtils';
import { stationDayEnd, stationInstant } from '../../../utils/stationTime';

// Image rules mirror the backend allowlist (isAllowedBrandingImage): image MIME
// types up to 2 MB.
const ALLOWED_BANNER_MIME = new Set([
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/svg+xml',
]);
const MAX_BANNER_FILE_BYTES = 2 * 1024 * 1024;

// Bookable end-user target flows mapped to the tenant service (use case) that
// enables them. The select is gated to the services the current tenant offers;
// when the tenant config can't be resolved we show all flows (fallback below).
const TARGET_FLOWS = [
    'equipmentRental/book',
    'parcelDelivery/book',
    'roomRental/book',
    'venue/book',
    'hall/book',
    'key-handover/book',
    'sup/book',
    'p2p/book',
    'temStorage/book',
] as const;

const FLOW_SERVICE: Record<(typeof TARGET_FLOWS)[number], UseCase> = {
    'equipmentRental/book': 'rental',
    'parcelDelivery/book': 'parcel',
    'roomRental/book': 'apartment',
    'venue/book': 'venue',
    'hall/book': 'hall',
    'key-handover/book': 'keys',
    'sup/book': 'sup',
    'p2p/book': 'p2p',
    'temStorage/book': 'storage',
};

interface Props {
    open: boolean;
    onClose: () => void;
    /** When set the modal edits this banner; otherwise it creates a new one. */
    banner?: AdminBanner | null;
}

const fieldClass =
    'w-full border border-foreground/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary';

// Secondary (outline) button used for the inline "create promo code" action.
const outlineButtonClass =
    'rounded-md border border-foreground/15 px-3 py-2 text-sm font-medium text-foreground/70 hover:text-foreground';


// The per-language image slots the form manages. Extensible: adding a language
// is a matter of adding its code here (plus the matching backend field).
type ImageLang = 'de' | 'en';
const IMAGE_LANGS: ImageLang[] = ['de', 'en'];

// The per-language image slots render through the shared ImageSlot
// (components/ImageSlot.tsx), with the end-user carousel's 16:7 ratio.

const BannerFormModal = ({ open, onClose, banner }: Props) => {
    const { t } = useTranslation();
    const isEdit = !!banner;

    // Per-language image state: a newly-picked file replaces that language, a
    // `removed` flag (edit only) drops the existing one. Single source of truth.
    const [imageFiles, setImageFiles] = useState<Record<ImageLang, File | null>>({
        de: null,
        en: null,
    });
    const [removed, setRemoved] = useState<Record<ImageLang, boolean>>({
        de: false,
        en: false,
    });
    const [type, setType] = useState<BannerType>('explainer');
    const [href, setHref] = useState('');
    const [voucherCode, setVoucherCode] = useState('');
    const [active, setActive] = useState(true);
    const [startsAt, setStartsAt] = useState('');
    const [endsAt, setEndsAt] = useState('');
    const [order, setOrder] = useState('0');
    const [formError, setFormError] = useState<string | null>(null);
    const [createPromoOpen, setCreatePromoOpen] = useState(false);
    // Creating the code from here is a voucher write (manage:vouchers).
    const canManageVouchers = useSelector(makeHasPermissionSelector(Permission.manageVouchers()));

    const [createBanner, { isLoading: creating }] = useCreateBannerMutation();
    const [updateBanner, { isLoading: updating }] = useUpdateBannerMutation();
    const isLoading = creating || updating;

    // Promotional codes the promo banner can reference: non-expired PROMOTIONAL
    // codes only. A null `validUntilDate` means no expiry. Campaign codes
    // (#2249) are excluded — each belongs to one recipient, and a banner shows
    // its code to everyone.
    const { data: vouchers } = useGetVouchersQuery();
    const promoCodes = useMemo(() => {
        const now = Date.now();
        return (vouchers ?? [])
            .filter((v) => v.codeType === 'PROMOTIONAL' && !!v.code && !v.campaignId)
            .filter((v) => !v.validUntilDate || new Date(v.validUntilDate).getTime() > now)
            .map((v) => v.code as string);
    }, [vouchers]);

    // Target flows gated to the current tenant's services; fall back to all
    // flows when the config can't be resolved (don't lock the user out).
    const { services, usesPaymentCodes } = useTenantCapabilities();
    const targetFlows = useMemo(() => {
        if (services.length === 0) return [...TARGET_FLOWS];
        return TARGET_FLOWS.filter((flow) => services.includes(FLOW_SERVICE[flow]));
    }, [services]);

    // Initialize from the edited banner (or reset for create) whenever the modal
    // opens or the target banner changes. Single source of truth per field.
    useEffect(() => {
        if (!open) return;
        setImageFiles({ de: null, en: null });
        setRemoved({ de: false, en: false });
        // No-payment tenants can't use promo banners; coerce a stored 'promo'
        // type back to 'explainer' so the hidden type button can't strand it.
        setType(
            banner?.type === 'promo' && !usesPaymentCodes
                ? 'explainer'
                : banner?.type ?? 'explainer',
        );
        setHref(banner?.href ?? '');
        setVoucherCode(banner?.voucherCode ?? '');
        setActive(banner?.active ?? true);
        setStartsAt(instantToLocalDateInputValue(banner?.startsAt));
        setEndsAt(instantToLocalDateInputValue(banner?.endsAt));
        setOrder(String(banner?.order ?? 0));
        setFormError(null);
    }, [open, banner, usesPaymentCodes]);

    // Object-URL previews for newly-picked files; revoked on change/unmount.
    const filePreviews = useMemo(
        () => ({
            de: imageFiles.de ? URL.createObjectURL(imageFiles.de) : null,
            en: imageFiles.en ? URL.createObjectURL(imageFiles.en) : null,
        }),
        [imageFiles],
    );
    useEffect(() => () => {
        if (filePreviews.de) URL.revokeObjectURL(filePreviews.de);
        if (filePreviews.en) URL.revokeObjectURL(filePreviews.en);
    }, [filePreviews]);

    // Per language: show the newly-picked file, else the existing stored image
    // (unless it was removed in this edit session), else nothing.
    const previewSrcFor = (lang: ImageLang): string | null =>
        filePreviews[lang] ?? (removed[lang] ? null : banner?.images?.[lang] ?? null);

    const validateFile = (file: File): string | null => {
        if (!ALLOWED_BANNER_MIME.has(file.type.toLowerCase())) {
            return t('banners.form.image_invalid_type');
        }
        if (file.size > MAX_BANNER_FILE_BYTES) {
            return t('banners.form.image_too_large');
        }
        return null;
    };

    const handleSelectFile = (lang: ImageLang) => (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        e.target.value = '';
        if (!file) return;
        const err = validateFile(file);
        if (err) {
            setFormError(err);
            return;
        }
        setFormError(null);
        setImageFiles((prev) => ({ ...prev, [lang]: file }));
        // Picking a file un-removes that language.
        setRemoved((prev) => ({ ...prev, [lang]: false }));
    };

    // Remove that language's image: drop a freshly-picked file, and on edit flag
    // the existing stored image for removal.
    const handleRemoveImage = (lang: ImageLang) => {
        setImageFiles((prev) => ({ ...prev, [lang]: null }));
        if (isEdit && banner?.images?.[lang]) {
            setRemoved((prev) => ({ ...prev, [lang]: true }));
        }
    };

    const close = () => {
        onClose();
    };

    // Field gating by type: explainer → href only; promo → href + voucher code;
    // notice → neither (image-only, informational).
    const showHref = type === 'explainer' || type === 'promo';
    const showVoucher = type === 'promo';

    // Validation derives from form state (not API data). At least one language
    // image must remain: a newly-picked file, OR an existing stored image that
    // wasn't removed in this session. Order must be a finite number.
    // explainer/promo require a non-empty href; promo also requires a non-empty
    // voucher code; notice requires neither.
    const hasImageFor = (lang: ImageLang): boolean =>
        !!imageFiles[lang] || (!removed[lang] && !!banner?.images?.[lang]);
    const hasAnyImage = IMAGE_LANGS.some((lang) => hasImageFor(lang));
    const orderNum = Number(order);
    const isValid =
        hasAnyImage &&
        Number.isFinite(orderNum) &&
        (!showHref || href.trim().length > 0) &&
        (!showVoucher || voucherCode.trim().length > 0);

    const handleSubmit = async () => {
        setFormError(null);
        if (!isValid) {
            setFormError(t('banners.form.validation'));
            return;
        }
        // The date inputs yield a local 'yyyy-mm-dd'. The app filter hides a
        // banner once now < startsAt or now > endsAt, so treat the picked dates
        // as local day boundaries: a banner is visible from the start of its
        // start day through the end of its end day. Serializing the bare date
        // (start-of-day UTC) would otherwise end the banner a day early and
        // shift the cut-off by the viewer's timezone. Mirrors the promo-code
        // end-of-day handling.
        // Station days (ADR-0019): starts at station midnight, ends at the end
        // of the station day — read back by `instantToLocalDateInputValue`.
        const startsAtIso = startsAt ? stationInstant(startsAt).toISOString() : '';
        const endsAtIso = endsAt ? stationDayEnd(endsAt).toISOString() : '';
        // Notice carries neither href nor voucher; explainer carries href only.
        const hrefToSend = showHref ? href : '';
        const voucherToSend = showVoucher ? voucherCode : '';
        try {
            if (isEdit && banner) {
                await updateBanner({
                    id: banner.id,
                    ...(imageFiles.de ? { imageDe: imageFiles.de } : {}),
                    ...(imageFiles.en ? { imageEn: imageFiles.en } : {}),
                    ...(removed.de ? { removeDe: true } : {}),
                    ...(removed.en ? { removeEn: true } : {}),
                    type,
                    href: hrefToSend,
                    voucherCode: voucherToSend,
                    active,
                    startsAt: startsAtIso,
                    endsAt: endsAtIso,
                    order: orderNum,
                }).unwrap();
            } else {
                await createBanner({
                    ...(imageFiles.de ? { imageDe: imageFiles.de } : {}),
                    ...(imageFiles.en ? { imageEn: imageFiles.en } : {}),
                    type,
                    href: hrefToSend,
                    voucherCode: voucherToSend,
                    active,
                    startsAt: startsAtIso,
                    endsAt: endsAtIso,
                    order: orderNum,
                }).unwrap();
            }
            close();
        } catch (err) {
            const message =
                typeof err === 'object' &&
                err !== null &&
                'data' in err &&
                typeof (err as { data?: { message?: string } }).data?.message === 'string'
                    ? (err as { data: { message: string } }).data.message
                    : t('banners.form.error');
            setFormError(message);
        }
    };

    // Segmented two/three-button picker, mirroring CreateCodeModal's
    // discount-type toggle (active = tenant primary, inactive = muted outline).
    const typeButton = (value: BannerType, label: string) => (
        <button
            type={'button'}
            onClick={() => setType(value)}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                type === value
                    ? 'bg-primary text-text-buttonContrast'
                    : 'border border-foreground/15 text-foreground/60 hover:text-foreground'
            }`}
        >
            {label}
        </button>
    );

    return (
        <CustomModal
            open={open}
            title={isEdit ? t('banners.form.edit_title') : t('banners.form.create_title')}
            onClose={close}
            closeButton={
                <CustomButton title={t('common.cancel')} isFilled={false} onClick={close} />
            }
            saveButton={
                <CustomButton
                    title={t('common.save')}
                    loading={isLoading}
                    disabled={!isValid || isLoading}
                    onClick={handleSubmit}
                />
            }
        >
            <div className={'flex flex-col gap-4'}>
                <div>
                    <LabelWithHelp
                        text={t('banners.form.type_label')}
                        help={t('banners.help.type')}
                    />
                    <div className={'flex gap-2'}>
                        {typeButton('explainer', t('banners.types.explainer'))}
                        {usesPaymentCodes && typeButton('promo', t('banners.types.promo'))}
                        {typeButton('notice', t('banners.types.notice'))}
                    </div>
                </div>

                <div className={'flex flex-col gap-4 sm:flex-row'}>
                    {IMAGE_LANGS.map((lang) => (
                        <ImageSlot
                            key={lang}
                            label={t(`banners.form.image_${lang}_label`)}
                            help={t('banners.help.image')}
                            previewSrc={previewSrcFor(lang)}
                            onSelect={handleSelectFile(lang)}
                            onRemove={() => handleRemoveImage(lang)}
                            chooseLabel={t('banners.form.choose_image')}
                            removeLabel={t('banners.form.remove_image')}
                            noImageLabel={t('banners.form.no_image')}
                            aspectClass={'aspect-[16/7]'}
                            targetRatio={16 / 7}
                            ratioWarning={t('banners.form.ratio_warning')}
                        />
                    ))}
                </div>
                {!hasAnyImage && (
                    <p className={'text-sm text-red-500'}>
                        {t('banners.form.image_required')}
                    </p>
                )}

                {showHref && (
                    <div>
                        <LabelWithHelp
                            text={t('banners.form.target_label')}
                            help={t('banners.help.target')}
                        />
                        <select
                            className={fieldClass}
                            value={href}
                            onChange={(e) => setHref(e.target.value)}
                        >
                            <option value={''}>{t('banners.form.target_none')}</option>
                            {targetFlows.map((flow) => (
                                <option key={flow} value={`/${flow}`}>
                                    {t(`banners.flows.${flow}`)}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {showVoucher && (
                    <div>
                        <LabelWithHelp
                            text={t('banners.form.promo_code_label')}
                            help={t('banners.help.promo_code')}
                        />
                        {promoCodes.length === 0 ? (
                            <div className={'flex flex-col gap-2'}>
                                <p className={'text-sm text-foreground/50'}>
                                    {t('banners.form.promo_code_empty')}
                                </p>
                                {canManageVouchers && (
                                    <button
                                        type={'button'}
                                        onClick={() => setCreatePromoOpen(true)}
                                        className={`self-start ${outlineButtonClass}`}
                                    >
                                        {t('banners.form.promo_code_create')}
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className={'flex gap-2'}>
                                <select
                                    className={fieldClass}
                                    value={voucherCode}
                                    onChange={(e) => setVoucherCode(e.target.value)}
                                >
                                    <option value={''}>{t('banners.form.promo_code_select')}</option>
                                    {promoCodes.map((code) => (
                                        <option key={code} value={code}>
                                            {code}
                                        </option>
                                    ))}
                                </select>
                                {canManageVouchers && (
                                    <button
                                        type={'button'}
                                        onClick={() => setCreatePromoOpen(true)}
                                        className={`whitespace-nowrap ${outlineButtonClass}`}
                                    >
                                        {t('banners.form.promo_code_create')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                <div className={'flex gap-3'}>
                    <div className={'flex-1'}>
                        <LabelWithHelp
                            text={t('banners.form.starts_at_label')}
                            help={t('banners.help.starts_at')}
                        />
                        <input
                            className={fieldClass}
                            type={'date'}
                            value={startsAt}
                            onChange={(e) => setStartsAt(e.target.value)}
                        />
                    </div>
                    <div className={'flex-1'}>
                        <LabelWithHelp
                            text={t('banners.form.ends_at_label')}
                            help={t('banners.help.ends_at')}
                        />
                        <input
                            className={fieldClass}
                            type={'date'}
                            value={endsAt}
                            onChange={(e) => setEndsAt(e.target.value)}
                        />
                    </div>
                </div>

                <div>
                    <LabelWithHelp
                        text={t('banners.form.order_label')}
                        help={t('banners.help.order')}
                    />
                    <input
                        className={fieldClass}
                        type={'number'}
                        step={1}
                        value={order}
                        onChange={(e) => setOrder(e.target.value)}
                    />
                </div>

                <div className={'flex items-center gap-1.5'}>
                    <label className={'flex items-center gap-2 text-sm text-foreground/70'}>
                        <input
                            type={'checkbox'}
                            checked={active}
                            onChange={(e) => setActive(e.target.checked)}
                        />
                        {t('banners.form.active_label')}
                    </label>
                    <InfoTooltip text={t('banners.help.active')} />
                </div>

                {formError && <p className={'text-sm text-red-500'}>{formError}</p>}
            </div>

            <CreateCodeModal
                open={createPromoOpen}
                onClose={() => setCreatePromoOpen(false)}
                onCreated={(code) => setVoucherCode(code)}
            />
        </CustomModal>
    );
};

export default BannerFormModal;
